import type { ChatbotListItem, InternalSalesReferenceContent } from '@neural-intelligence/shared';

// In development, uses Vite proxy (/api -> localhost:8787)
// In production, set VITE_API_URL to your worker URL
const API_BASE = import.meta.env.VITE_API_URL 
  ? `${import.meta.env.VITE_API_URL}/api` 
  : '/api';

async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ success: boolean; data?: T; error?: string; detail?: string | null }> {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        ...options.headers,
        ...(options.body && !(options.body instanceof FormData)
          ? { 'Content-Type': 'application/json' }
          : {}),
      },
    });

    try {
      const data = await response.json();
      return data;
    } catch {
      // Response wasn't JSON
      return { 
        success: false, 
        error: response.ok ? 'Invalid response from server' : `Server error: ${response.status}` 
      };
    }
  } catch (error) {
    return { success: false, error: 'Network error. Please check your connection.' };
  }
}

// Auth
export async function login(email: string, password: string) {
  return fetchAPI<{ id: string; email: string; role: string; assigned_chatbot_id: string | null }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function logout() {
  return fetchAPI('/auth/logout', { method: 'POST' });
}

export async function getMe() {
  return fetchAPI<{ id: string; email: string; role: string; assigned_chatbot_id: string | null }>('/me');
}

// Chatbots
export async function getChatbots() {
  return fetchAPI<ChatbotListItem[]>(
    '/chatbots'
  );
}

export async function getChatbot(id: string) {
  return fetchAPI<{
    id: string;
    name: string;
    title: string | null;
    instructions: string;
    cta_label: string | null;
    cta_url: string | null;
    greeting: string;
    theme_json: string | null;
    logo_url: string | null;
    website_url: string | null;
    vector_store_id: string | null;
    domains: string[];
    editable_by_sales?: boolean;
    is_testing?: boolean;
    customer_chatbot_enabled?: boolean;
    customer_email_responder_enabled?: boolean;
    analytics_enabled?: boolean;
    created_at: string;
    updated_at: string;
  }>(`/chatbots/${id}`);
}

export async function getChatbotStats(id: string) {
  return fetchAPI<{
    total: number;
    last24h: number;
    last7d: number;
    last30d: number;
  }>(`/chatbots/${id}/stats`);
}

export async function createChatbot(data: { name: string }) {
  return fetchAPI<{ id: string; name: string }>('/chatbots', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateChatbot(
  id: string,
  data: {
    name?: string;
    title?: string;
    instructions?: string;
    cta_label?: string | null;
    cta_url?: string | null;
    greeting?: string;
    theme_json?: string;
    logo_url?: string | null;
    website_url?: string;
    editable_by_sales?: boolean;
    is_testing?: boolean;
    customer_chatbot_enabled?: boolean;
    customer_email_responder_enabled?: boolean;
    analytics_enabled?: boolean;
  }
) {
  return fetchAPI(`/chatbots/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteChatbot(id: string) {
  return fetchAPI(`/chatbots/${id}`, { method: 'DELETE' });
}

// Domains
export async function addDomain(chatbotId: string, domain: string) {
  return fetchAPI<{ id: string; domain: string }>(`/chatbots/${chatbotId}/domains`, {
    method: 'POST',
    body: JSON.stringify({ domain }),
  });
}

export async function removeDomain(chatbotId: string, domain: string) {
  return fetchAPI(`/chatbots/${chatbotId}/domains/${encodeURIComponent(domain)}`, {
    method: 'DELETE',
  });
}

// Files
export async function uploadPDF(chatbotId: string, file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return fetchAPI<{ id: string; filename: string; status: string }>(
    `/chatbots/${chatbotId}/upload-pdf`,
    {
      method: 'POST',
      body: formData,
    }
  );
}

export async function getChatbotFiles(chatbotId: string) {
  return fetchAPI<
    Array<{
      id: string;
      filename: string;
      status: string;
      created_at: string;
    }>
  >(`/chatbots/${chatbotId}/files`);
}

export async function deleteFile(chatbotId: string, fileId: string) {
  return fetchAPI(`/chatbots/${chatbotId}/files/${fileId}`, {
    method: 'DELETE',
  });
}

// FAQs
export interface FAQ {
  id: string;
  chatbot_id: string;
  question: string;
  answer: string;
  sort_order: number;
  created_at: string;
}

export async function getFAQs(chatbotId: string) {
  return fetchAPI<FAQ[]>(`/chatbots/${chatbotId}/faqs`);
}

export async function createFAQ(
  chatbotId: string,
  data: { question: string; answer: string; sort_order?: number }
) {
  return fetchAPI<FAQ>(`/chatbots/${chatbotId}/faqs`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateFAQ(
  chatbotId: string,
  faqId: string,
  data: { question?: string; answer?: string; sort_order?: number }
) {
  return fetchAPI<FAQ>(`/chatbots/${chatbotId}/faqs/${faqId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteFAQ(chatbotId: string, faqId: string) {
  return fetchAPI(`/chatbots/${chatbotId}/faqs/${faqId}`, {
    method: 'DELETE',
  });
}

// Website extraction
export async function extractWebsiteInfo(chatbotId: string, url: string) {
  return fetchAPI<{ description: string }>(`/chatbots/${chatbotId}/extract-website`, {
    method: 'POST',
    body: JSON.stringify({ url }),
  });
}

// Settings
export async function getSettings() {
  return fetchAPI<Record<string, string>>('/settings');
}

export async function updateSettings(senderEmail: string) {
  return fetchAPI('/settings', {
    method: 'PUT',
    body: JSON.stringify({ sender_email: senderEmail }),
  });
}

export async function testEmail(recipient: string) {
  return fetchAPI('/settings/test-email', {
    method: 'POST',
    body: JSON.stringify({ recipient }),
  });
}

export interface SalesReferenceAccessSettings {
  sales_reference_enabled: boolean;
}

export interface SalesReferenceContentSettings {
  content: InternalSalesReferenceContent | null;
}

export async function getSalesReferenceAccess() {
  return fetchAPI<SalesReferenceAccessSettings>('/settings/sales-reference-access');
}

export async function updateSalesReferenceAccess(salesReferenceEnabled: boolean) {
  return fetchAPI('/settings/sales-reference-access', {
    method: 'PUT',
    body: JSON.stringify({ sales_reference_enabled: salesReferenceEnabled }),
  });
}

export async function getSalesReferenceContent() {
  return fetchAPI<SalesReferenceContentSettings>('/settings/sales-reference-content');
}

export async function updateSalesReferenceContent(content: InternalSalesReferenceContent) {
  return fetchAPI<SalesReferenceContentSettings>('/settings/sales-reference-content', {
    method: 'PUT',
    body: JSON.stringify({ content }),
  });
}

export async function getSessions() {
  return fetchAPI<Array<{
    id: string;
    ip_address: string;
    user_agent: string;
    country: string | null;
    city: string | null;
    created_at: string;
    last_active: string;
  }>>('/settings/sessions');
}

export async function revokeSession(sessionId: string) {
  return fetchAPI(`/settings/sessions/${sessionId}`, {
    method: 'DELETE',
  });
}

// Report Emails
export async function addReportEmail(chatbotId: string, email: string) {
  return fetchAPI<{ emails: string[] }>(`/chatbots/${chatbotId}/report-emails`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function removeReportEmail(chatbotId: string, email: string) {
  return fetchAPI<{ emails: string[] }>(`/chatbots/${chatbotId}/report-emails/${encodeURIComponent(email)}`, {
    method: 'DELETE',
  });
}

// Email Connection
export interface EmailConnection {
  id: string;
  chatbot_id: string;
  provider: 'gmail' | 'outlook' | 'forwarding';
  email_address: string | null;
  status: 'pending' | 'active' | 'disconnected' | 'error';
  forwarding_address: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmailConnectionResponse {
  connected: boolean;
  provider?: 'gmail' | 'outlook' | 'forwarding';
  email_address?: string | null;
  forwarding_address?: string | null;
  status?: 'pending' | 'active' | 'disconnected' | 'error';
  created_at?: string;
  updated_at?: string;
}

export async function getEmailConnection(chatbotId: string) {
  return fetchAPI<EmailConnectionResponse>(`/chatbots/${chatbotId}/email-connection`);
}

export async function connectEmail(chatbotId: string, provider: 'gmail' | 'outlook' | 'forwarding') {
  return fetchAPI<{ id?: string; provider?: string; status?: string; forwarding_address?: string; authorization_url?: string }>(
    `/chatbots/${chatbotId}/email-connection/connect`,
    { method: 'POST', body: JSON.stringify({ provider }) }
  );
}

export async function disconnectEmail(chatbotId: string) {
  return fetchAPI(`/chatbots/${chatbotId}/email-connection/disconnect`, { method: 'POST' });
}

export interface EmailConnectionDiagnostics {
  connected: boolean;
  health: 'not_connected' | 'connected' | 'connected_with_unread_mail' | 'connected_no_unread_mail' | 'reconnect_required' | 'provider_error';
  provider?: 'gmail' | 'outlook' | 'forwarding';
  email_address?: string | null;
  forwarding_address?: string | null;
  status?: 'pending' | 'active' | 'disconnected' | 'error';
  token_expires_at?: string | null;
  connection_created_at?: string | null;
  connection_updated_at?: string | null;
  responder_enabled?: boolean;
  scan_interval_minutes?: number | null;
  exclude_newsletters?: boolean;
  issue_code?: 'unread_mail_visible' | 'no_unread_mail' | 'recent_read_mail_only' | 'mail_in_other_folder' | 'drafts_visible' | 'empty_mailbox' | 'possible_alias_or_delegated_mailbox';
  likely_cause?: string | null;
  warning?: string | null;
  provider_error_code?: 'token_refresh_failed' | 'token_missing' | 'token_decrypt_failed' | 'provider_auth_failed' | 'provider_permission_denied' | 'provider_rate_limited' | 'provider_unavailable' | 'provider_request_failed' | 'diagnostic_failed';
  provider_error?: string;
  provider_error_detail?: string | null;
  last_scan?: {
    status: 'success' | 'error' | 'skipped';
    trigger_type: string;
    started_at: string;
    finished_at: string | null;
    provider_unread_count: number;
    fetched_count: number;
    duplicate_skipped_count: number;
    newsletter_skipped_count: number;
    drafts_created_count: number;
    error_count: number;
    error_message: string | null;
  } | null;
  provider_diagnostics?: {
    provider: 'gmail' | 'outlook';
    mailboxIdentity?: Record<string, string | number | null>;
    inboxUnreadEstimate?: number;
    inboxUnreadSampleCount?: number;
    recentInboxSampleCount?: number;
    draftSampleCount?: number;
    unreadSamples?: Array<{
      receivedAt?: string | null;
      sender?: string | null;
      senderDomain?: string | null;
      subject?: string | null;
      isRead?: boolean;
      labelIds?: string[];
      inferenceClassification?: string | null;
    }>;
    recentInboxSamples?: Array<{
      receivedAt?: string | null;
      sender?: string | null;
      senderDomain?: string | null;
      subject?: string | null;
      isRead?: boolean;
      labelIds?: string[];
      inferenceClassification?: string | null;
    }>;
    draftSamples?: Array<{ updatedAt?: string | null; to?: string[]; subject?: string | null }>;
    mailboxSettings?: {
      timeZone: string | null;
      language: string | null;
      dateFormat: string | null;
      timeFormat: string | null;
      automaticRepliesStatus: string | null;
    } | null;
    messageRules?: Array<{ displayName: string | null; isEnabled: boolean | null; sequence: number | null }>;
    labels?: Array<{ label: string; type?: string | null; messagesUnread: number | null; messagesTotal?: number | null; threadsUnread: number | null; threadsTotal?: number | null }>;
    folders?: Array<{ folder: string; displayName?: string | null; unreadItemCount: number | null; totalItemCount: number | null; errorStatus?: number }>;
  } | null;
}

export async function getEmailConnectionDiagnostics(chatbotId: string) {
  return fetchAPI<EmailConnectionDiagnostics>(`/chatbots/${chatbotId}/email-connection/diagnostics`);
}

export interface EmailDiagnosticTest {
  id: string;
  status: 'sent' | 'manual_required' | 'found' | 'not_found' | 'error';
  recipient: string;
  code: string;
  subject: string;
  body: string;
  sent_at: string | null;
  manual_required: boolean;
  instructions: string;
}

export interface EmailDiagnosticTestCheck {
  id: string;
  status: 'found' | 'not_found';
  outcome: 'found_unread_inbox' | 'found_read_inbox' | 'found_other_folder' | 'not_found';
  code: string;
  recipient: string;
  checked_at: string;
  detected_folder: string | null;
  detected_read_state: 'read' | 'unread' | null;
  received_at: string | null;
  sender: string | null;
  sender_domain: string | null;
  subject: string | null;
  message: string;
}

export async function createEmailDiagnosticTest(chatbotId: string) {
  return fetchAPI<EmailDiagnosticTest>(`/chatbots/${chatbotId}/email-connection/diagnostic-test`, { method: 'POST' });
}

export async function checkEmailDiagnosticTest(chatbotId: string, testId: string) {
  return fetchAPI<EmailDiagnosticTestCheck>(`/chatbots/${chatbotId}/email-connection/diagnostic-test/${testId}/check`, { method: 'POST' });
}

export interface EmailDraftCreationTest {
  provider: 'gmail' | 'outlook';
  draft_id: string;
  message_id: string;
  recipient: string;
  subject: string;
  body: string;
  created_at: string;
  verified: boolean;
  message: string;
}

export async function testEmailDraftCreation(chatbotId: string) {
  return fetchAPI<EmailDraftCreationTest>(`/chatbots/${chatbotId}/email-connection/test-draft`, { method: 'POST' });
}

// Email Responder Config
export interface EmailResponderConfig {
  id?: string;
  chatbot_id: string;
  enabled: boolean;
  response_tone: 'formal' | 'professional' | 'friendly' | 'concise';
  email_signature: string;
  scan_interval_minutes: number;
  cc_forwarder_on_forward: boolean;
  exclude_newsletters: boolean;
}

export async function getEmailResponderConfig(chatbotId: string) {
  return fetchAPI<EmailResponderConfig>(`/chatbots/${chatbotId}/email-responder-config`);
}

export async function updateEmailResponderConfig(chatbotId: string, data: Partial<EmailResponderConfig>) {
  return fetchAPI<EmailResponderConfig>(`/chatbots/${chatbotId}/email-responder-config`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Training Emails
export interface TrainingEmail {
  id: string;
  chatbot_id: string;
  subject: string;
  incoming_body: string;
  response_body: string;
  created_at: string;
}

export async function getTrainingEmails(chatbotId: string) {
  return fetchAPI<TrainingEmail[]>(`/chatbots/${chatbotId}/training-emails`);
}

export async function addTrainingEmail(chatbotId: string, data: { subject?: string; incoming_body: string; response_body: string }) {
  return fetchAPI<TrainingEmail>(`/chatbots/${chatbotId}/training-emails`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteTrainingEmail(chatbotId: string, emailId: string) {
  return fetchAPI(`/chatbots/${chatbotId}/training-emails/${emailId}`, { method: 'DELETE' });
}

export interface TrainingEmailUploadResult {
  created: TrainingEmail[];
  parsed: { subject: string; incoming_body: string; response_body: string } | null;
  errors: { error: string }[];
}

export async function uploadTrainingEmailFiles(
  chatbotId: string,
  files: { incoming?: File; response?: File; pdf?: File | File[] }
) {
  const formData = new FormData();
  if (files.incoming) formData.append('incoming_file', files.incoming);
  if (files.response) formData.append('response_file', files.response);
  if (Array.isArray(files.pdf)) {
    for (const pdf of files.pdf) formData.append('pdf_file', pdf);
  } else if (files.pdf) {
    formData.append('pdf_file', files.pdf);
  }
  return fetchAPI<TrainingEmailUploadResult>(`/chatbots/${chatbotId}/training-emails/upload`, {
    method: 'POST',
    body: formData,
  });
}

// Email Responder Preview
export async function previewEmailResponse(
  chatbotId: string,
  emailBody: string,
  options?: { fromName?: string; fromEmail?: string; subject?: string }
) {
  return fetchAPI<{
    preview: boolean;
    message: string;
    from_name: string | null;
    from_email: string;
    subject: string;
    email_body: string;
  }>(`/chatbots/${chatbotId}/email-responder/preview`, {
    method: 'POST',
    body: JSON.stringify({
      email_body: emailBody,
      from_name: options?.fromName,
      from_email: options?.fromEmail,
      subject: options?.subject,
    }),
  });
}

// Suggest an incoming email for testing
export async function suggestIncomingEmail(chatbotId: string) {
  return fetchAPI<{ subject: string; body: string }>(`/chatbots/${chatbotId}/email-responder/suggest-email`, {
    method: 'POST',
  });
}

// Email Drafts
export interface EmailDraft {
  id: string;
  chatbot_id: string;
  original_from: string;
  original_subject: string;
  original_body?: string;
  draft_body?: string;
  draft_to?: string;
  draft_cc?: string;
  draft_message_id?: string;
  status: string;
  error_message?: string;
  created_at: string;
}

export async function getEmailDrafts(chatbotId: string, limit = 50, offset = 0): Promise<{ success: boolean; data?: EmailDraft[]; total?: number; error?: string }> {
  return fetchAPI(`/chatbots/${chatbotId}/email-drafts?limit=${limit}&offset=${offset}`) as any;
}

export async function updateEmailDraft(chatbotId: string, draftId: string, draftBody: string): Promise<{ success: boolean; error?: string }> {
  return fetchAPI(`/chatbots/${chatbotId}/email-drafts/${draftId}`, {
    method: 'PATCH',
    body: JSON.stringify({ draft_body: draftBody }),
  }) as any;
}

// Customer Management
export async function createCustomer(data: { email: string; password: string; assigned_chatbot_id: string }) {
  return fetchAPI<{ id: string; email: string; role: string; assigned_chatbot_id: string }>(
    '/auth/users/customer',
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export interface ChatbotCustomer {
  id: string;
  email: string;
  created_at: string;
}

export async function getChatbotCustomers(chatbotId: string) {
  return fetchAPI<ChatbotCustomer[]>(`/chatbots/${chatbotId}/customers`);
}

export async function deleteChatbotCustomer(chatbotId: string, userId: string) {
  return fetchAPI(`/chatbots/${chatbotId}/customers/${userId}`, { method: 'DELETE' });
}

export async function forgotPassword(email: string) {
  return fetchAPI('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function changePassword(currentPassword: string, newPassword: string) {
  return fetchAPI('/auth/change-password', {
    method: 'PUT',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
}

// Analytics
export interface AnalyticsOverview {
  total: number;
  last24h: number;
  last7d: number;
  last30d: number;
  prev24h: number;
  prev7d: number;
  prev30d: number;
}

export interface DailyCount {
  day: string;
  conversations: number;
  drafts: number;
}

export interface ChatbotBreakdown {
  id: string;
  name: string;
  total_conversations: number;
  last7d_conversations: number;
  total_drafts: number;
  last7d_drafts: number;
}

export interface MessageStats {
  total_messages: number;
  user_messages: number;
  assistant_messages: number;
  avg_messages_per_conversation: number;
}

export interface EmailDraftStats {
  total: number;
  errors: number;
  last24h: number;
  last7d: number;
  last30d: number;
  prev24h: number;
  prev7d: number;
  prev30d: number;
}

export async function getAnalyticsOverview() {
  return fetchAPI<AnalyticsOverview>('/analytics/overview');
}

export async function getAnalyticsDaily() {
  return fetchAPI<DailyCount[]>('/analytics/daily');
}

export async function getChatbotBreakdown() {
  return fetchAPI<ChatbotBreakdown[]>('/analytics/chatbot-breakdown');
}

export async function getEmailDraftStats() {
  return fetchAPI<EmailDraftStats>('/analytics/email-drafts');
}

export async function getChatbotDailyStats(chatbotId: string) {
  return fetchAPI<DailyCount[]>(`/chatbots/${chatbotId}/stats/daily`);
}

export async function getChatbotMessageStats(chatbotId: string) {
  return fetchAPI<MessageStats>(`/chatbots/${chatbotId}/stats/messages`);
}

export async function getChatbotEmailDraftStats(chatbotId: string) {
  return fetchAPI<EmailDraftStats>(`/chatbots/${chatbotId}/stats/email-drafts`);
}

// Activity Log
export interface ActivityLogEntry {
  id: string;
  chatbot_id: string | null;
  user_id: string;
  user_email: string;
  user_role: string | null;
  action: string;
  details: string | null;
  ip_address: string | null;
  created_at: string;
}

export async function getActivityLog(params?: {
  chatbot_id?: string;
  user_email?: string;
  action?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}): Promise<{ success: boolean; data?: ActivityLogEntry[]; total?: number; error?: string }> {
  const query = new URLSearchParams();
  if (params) {
    for (const [key, val] of Object.entries(params)) {
      if (val !== undefined && val !== '') query.set(key, String(val));
    }
  }
  return fetchAPI(`/activity-log?${query.toString()}`) as any;
}

// Data Retention
export interface RetentionSettings {
  data_retention_days: number;
  email_draft_retention_days: number;
  counts: {
    conversations: number;
    messages: number;
    drafts: number;
  };
}

export async function getRetentionSettings() {
  return fetchAPI<RetentionSettings>('/settings/retention');
}

export async function updateRetentionSettings(data: { data_retention_days: number; email_draft_retention_days: number }) {
  return fetchAPI('/settings/retention', { method: 'PUT', body: JSON.stringify(data) });
}

export async function inviteUser(data: { email: string; role: string; assigned_chatbot_id?: string }) {
  return fetchAPI<{ id: string; email: string; role: string; assigned_chatbot_id: string | null }>(
    '/auth/invite',
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function setPasswordWithToken(token: string, password: string) {
  return fetchAPI('/auth/set-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}
